import { useEffect, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import Markdown from 'markdown-to-jsx';
import { getArticle } from '../data/articles';

const ORIGIN = 'https://theloftsalontemecula.com';

// Internal links render as client-side <Link>; external open in a new tab.
const SmartLink = ({ href = '', children }) =>
  href.startsWith('/') ? (
    <Link to={href}>{children}</Link>
  ) : (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );

const Article = () => {
  const { slug } = useParams();
  const article = getArticle(slug);

  useEffect(() => {
    if (article) document.title = article.metaTitle;
  }, [article]);

  if (!article) {
    return (
      <div className="page">
        <section className="section">
          <div className="container text-center">
            <h1>We couldn't find that story.</h1>
            <p className="lede" style={{ marginInline: 'auto', marginTop: 'var(--space-4)' }}>
              It may have moved. Head back to the journal to see what's there.
            </p>
            <Link to="/journal" className="btn btn--primary" style={{ marginTop: 'var(--space-6)' }}>
              Back to the Journal
            </Link>
          </div>
        </section>
      </div>
    );
  }

  const schema = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.metaDescription,
    datePublished: article.date,
    dateModified: article.date,
    articleSection: article.category,
    author: { '@type': 'Organization', name: 'The Loft Salon Temecula' },
    publisher: {
      '@type': 'Organization',
      name: 'The Loft Salon Temecula',
      logo: { '@type': 'ImageObject', url: `${ORIGIN}/logo512.png` },
    },
    mainEntityOfPage: `${ORIGIN}/journal/${article.slug}`,
    image: `${ORIGIN}/images/og-cover.jpg`,
  });

  return (
    <div className="page">
      <article>
        <header className="article-head section--dark grain">
          <div className="container article-head__inner">
            <Link to="/journal" className="article-head__back">
              ← The Journal
            </Link>
            <span className="kicker">{article.category}</span>
            <h1>{article.title}</h1>
            <p className="article-head__meta">{article.readMins} min read</p>
          </div>
        </header>

        <div className="section">
          <div className="container">
            <div className="article-body">
              <Markdown options={{ overrides: { a: { component: SmartLink } }, forceBlock: true, wrapper: Fragment }}>
                {article.bodyMarkdown}
              </Markdown>
            </div>
          </div>
        </div>

        <section className="section--tight section--warm">
          <div className="container text-center">
            <div className="reveal" style={{ maxWidth: '620px', marginInline: 'auto' }}>
              <span className="kicker" style={{ justifyContent: 'center' }}>The Loft Salon</span>
              <h2>Ready when you are.</h2>
              <p className="lede" style={{ marginInline: 'auto', marginBlock: 'var(--space-4) var(--space-6)' }}>
                Find the stylist whose work fits what you want, or reach out and
                we'll match you.
              </p>
              <div className="btn-row" style={{ justifyContent: 'center' }}>
                <Link to="/team" className="btn btn--primary">Find your stylist</Link>
                <Link to="/contact" className="btn btn--outline">Contact the salon</Link>
              </div>
            </div>
          </div>
        </section>
      </article>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: schema }} />
    </div>
  );
};

export default Article;
